import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Box, CircularProgress, IconButton } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import moment from 'moment'
import Header from '../../Header'
import Button from '../../Button'
import DataTable from '../../@Common/DataTable'
import { showModal, hideModal, showErrorNotification, showSuccessNotification } from '../../../redux/actions'
import {
    Link
} from '@mui/material'

const style = {
    headerSubItemLink: {
        color: '#0E5777',
        textDecorationColor: '#0E5777',
        cursor: 'pointer',
    },
}

const getStatusView = (params, statusLoading, handleClickRefresh) => {
    switch (params.value) {
        case 1:
            if (statusLoading[params.row.id]) {
                return <CircularProgress style={{ height: '14px', width: '14px', marginLeft: '10px' }} />
            }
            return (<>
                Pending &nbsp;
                <Link underline="always" sx={style.headerSubItemLink} onClick={() => handleClickRefresh(params.row.id)}>
                    Refresh status
                </Link>
            </>)

        case 2:
            return 'Completed'
        case 3:
            return 'Failed'
        default:
            return ''
    }
}
const THIRTY_DAYS_IN_MS = 30 * 24 * 60 * 60 * 1000 // 30 days in milliseconds

function PayoutProcessor({
    addImportAsync,
    getImportAsync,
    getImportsAsync,
    importTypeText,
    closeModal,
    openModal,
    modalKey,
}) {
    const dispatch = useDispatch()
    const [imports, setImports] = useState([])
    const [importState, setImportState] = useState({ count: 0, processing: false, errors: [] })
    const [statusLoading, setStatusLoading] = useState({})

    useEffect(() => {
        getImportsAsync().then(imports => setImports(imports))
    }, [importState.count])

    const openImportModal = (props) => openModal(modalKey, props)

    const onClickImport = () => {
        const { processing, errors } = importState
        openImportModal({
            errors,
            processing,
            type: importTypeText,
            // handlers
            onFileChange: () => {
                setImportState({ ...importState, processing: false, errors: [] })
            },
            onClickSubmit: async ({ importFile }) => {
                setImportState({ ...importState, processing: true, errors: [] })
                try {
                    const res = await addImportAsync(importFile)
                    if (!res?.data?.success) {
                        showErrorMessage()
                        return
                    }
                    setTimeout(() => {
                        dispatch(showSuccessNotification('Import uploaded successfully.'))
                        setImportState({
                            ...importState,
                            processing: false,
                            errors: [],
                            count: importState.count + 1
                        })
                        closeModal()
                    }, 1000)
                } catch (ex) {
                    showErrorMessage()
                }
            },
            onClickClose: () => {
                setImportState({ ...importState, processing: false, errors: [] })
                closeModal()
            },
        })
    }

    const onUploadCsv = () => {
        onClickImport()
    }

    const onClickRefresh = (id) => {
        setStatusLoading({ ...statusLoading, [id]: true })
        getImportAsync(id).then(newData => {
            const oldIndex = imports.findIndex(x => x.id === id)
            const oldData = imports[oldIndex]
            if (newData && oldData && oldData.status !== newData.status) {
                const fresh = [...imports]
                fresh[oldIndex] = newData
                setImports(fresh)
            }
            setStatusLoading({ ...statusLoading, [id]: false })
        })
    }

    const showErrorMessage = () => {
        setImportState({ ...importState, processing: false, errors: [] })
        dispatch(showErrorNotification('There was an error while attempting to import file.'))
    }

    const openDialogModal = (props) => dispatch(showModal('DIALOG', props))

    const rephraseDetail = (detail) => {
        const defaultDetail = 'An error occurred, could not process payout file'
        if (!detail) {
            return defaultDetail
        }
        if (detail === 'No NetSuite invoices found for Shopify payouts.') {
            return 'No invoices found for Shopify payouts'
        }
        if (detail === 'Payouts already imported.') {
            return 'No new payouts detected in file.'
        }
        if (detail.startsWith('Missing fields:')) {
            return 'Could not process due to missing fields'
        }
        return defaultDetail
    }

    const Details = ({ row }) => {
        const { details, fileName, processedOnUtc, status } = row
        const firstDetail = details && details.length > 0 ? details[0] : null
        if (status !== 3 || !firstDetail) {
            return null
        }
        return <>
            {rephraseDetail(firstDetail)}
            <IconButton
                aria-label="more information"
                color='primary'
                onClick={() => openDialogModal({
                    title: 'Payouts Processor',
                    confirmLabel: 'OK',
                    onClickCancel: () => { dispatch(hideModal()) },
                    hideConfirmAction: true,
                    children: (<>
                        <p>
                            <strong>File: </strong>{fileName}
                        </p>
                        <p>
                            <strong>Processed On: </strong>{processedOnUtc ? new Date(processedOnUtc).toLocaleString('en-US') : null}
                        </p>
                        <div>
                            <p><strong>Errors: </strong>{details.length === 1 ? details[0] : null}</p>
                            {details.length > 1 && (
                                <ul>
                                    {details.map((detail, index) => (
                                        <li key={index}>{detail}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </>)
                })}>
                <InfoIcon />
            </IconButton>
        </>
    }

    return (
        <Box>
            <Header title={`${importTypeText} Processor`} sx={{ mb: '0px' }}>
                <Box sx={{ mb: '14px' }}>
                    <Button
                        label="Upload CSV"
                        variant='contained'
                        color='primary'
                        onClick={onUploadCsv}
                    />
                </Box>
            </Header>
            <DataTable
                loading={false}
                rows={imports || []}
                rowCount={imports ? imports.length : 0}
                disableColumnFilter={true}
                pageSizeOptions={[10, 25, 50, 100, 250, 500]}
                columns={[
                    {
                        field: 'fileName',
                        headerName: 'File Name',
                        flex: 1,
                    },
                    {
                        field: 'processedOnUtc',
                        headerName: 'Processed On',
                        flex: 1,
                        valueGetter: (params) => new Date(params.row.processedOnUtc),
                        renderCell: (params) => {
                            const processedOn = new Date(params.row.processedOnUtc)
                            const diffTime = Math.abs(processedOn - new Date())
                            const m = moment(processedOn)
                            return <>{diffTime > THIRTY_DAYS_IN_MS ? m.format('LL') : m.fromNow()}</>
                        },
                    },
                    {
                        field: 'status',
                        headerName: 'Status',
                        flex: 1,
                        renderCell: (params) => getStatusView(params, statusLoading, onClickRefresh),
                    },
                    {
                        field: 'details',
                        headerName: 'Errors',
                        flex: 1,
                        renderCell: (params) => <Details row={params.row} />
                    }
                ]}
            />
        </Box>
    )
}

export default PayoutProcessor